import { Component } from '@theme/component';

/**
 * Personalization helpers: an optional character counter for the notes textarea and an
 * optional drag & drop file field. Both are wired only when their refs are present, so the
 * same element serves the card "notes + file" layout and the poster "image" layout.
 *
 * The <input type="file"> / <textarea> carry `properties[...]` names and are bound to the
 * add-to-cart form via `form`, so Shopify stores them on the line item — no app.
 *
 * @typedef {object} Refs
 * @property {HTMLTextAreaElement} [notes]
 * @property {HTMLElement} [counter]
 * @property {HTMLElement} [dropzone]
 * @property {HTMLInputElement} [file]
 * @property {HTMLElement} [filename]
 * @property {HTMLElement} [dropText]
 * @property {HTMLButtonElement} [browse]
 * @property {HTMLElement} [error]
 * @extends Component<Refs>
 */
class ProductPersonalization extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();
    const { notes, counter, dropzone, file, browse } = this.refs;

    // Character counter
    if (notes && counter) {
      this.#onInput = () => {
        counter.textContent = String(notes.value.length);
      };
      notes.addEventListener('input', this.#onInput);
      this.#onInput();
    }

    // Drag & drop file field
    if (dropzone && file) {
      this.#onBrowse = (event) => {
        event.stopPropagation();
        file.click();
      };
      this.#onZoneClick = () => file.click();
      this.#onKey = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          file.click();
        }
      };
      this.#onDragOver = (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      };
      this.#onDragLeave = () => dropzone.classList.remove('is-dragover');
      this.#onDrop = (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-dragover');
        const dropped = event.dataTransfer?.files;
        if (dropped && dropped.length) {
          file.files = dropped;
          this.#handleFile();
        }
      };
      this.#onChange = () => this.#handleFile();

      browse?.addEventListener('click', this.#onBrowse);
      dropzone.addEventListener('click', this.#onZoneClick);
      dropzone.addEventListener('keydown', this.#onKey);
      dropzone.addEventListener('dragover', this.#onDragOver);
      dropzone.addEventListener('dragleave', this.#onDragLeave);
      dropzone.addEventListener('drop', this.#onDrop);
      file.addEventListener('change', this.#onChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { notes, dropzone, file, browse } = this.refs;
    notes?.removeEventListener('input', this.#onInput);
    browse?.removeEventListener('click', this.#onBrowse);
    dropzone?.removeEventListener('click', this.#onZoneClick);
    dropzone?.removeEventListener('keydown', this.#onKey);
    dropzone?.removeEventListener('dragover', this.#onDragOver);
    dropzone?.removeEventListener('dragleave', this.#onDragLeave);
    dropzone?.removeEventListener('drop', this.#onDrop);
    file?.removeEventListener('change', this.#onChange);
  }

  #onInput = () => {};
  #onBrowse = () => {};
  #onZoneClick = () => {};
  #onKey = () => {};
  #onDragOver = () => {};
  #onDragLeave = () => {};
  #onDrop = () => {};
  #onChange = () => {};

  #handleFile() {
    const { file, filename, dropText, dropzone, error } = this.refs;
    const selected = file?.files?.[0];

    if (!selected) {
      this.#reset();
      return;
    }

    const maxMb = Number(this.dataset.maxMb) || 15;
    if (selected.size > maxMb * 1024 * 1024) {
      this.#showError(`This file is larger than ${maxMb} MB. Please choose a smaller file.`);
      if (file) file.value = '';
      this.#reset();
      return;
    }

    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (filename) {
      filename.textContent = selected.name;
      filename.hidden = false;
    }
    if (dropText) dropText.hidden = true;
    dropzone?.classList.add('has-file');
  }

  #reset() {
    const { filename, dropText, dropzone } = this.refs;
    if (filename) {
      filename.hidden = true;
      filename.textContent = '';
    }
    if (dropText) dropText.hidden = false;
    dropzone?.classList.remove('has-file');
  }

  #showError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define('product-personalization', ProductPersonalization);
}
